<template>
    <main class="invite-container">
        <h1> Hosted by <span class="host">{{ creator }}</span>,<br /> you are cordially invited to join as a <span
                class="role">Co-Host</span>. </h1>
        <section class="session-card" aria-label="Session Information">
            <BroadcastIcon />
            <div class="session-card-content">
                <span class="session-card-name">{{ eventName }}</span>
                <span class="session-card-lb"><span class="status-dot"></span>Your room is live</span>
            </div>
        </section>

        <!-- DID Login Section (only show when not logged in) -->
        <div v-if="showDidLogin && !didConnected && !isLoggedIn" class="did-login-section">
            <DidLogin
                :allow-guest="true"
                @connected="onDidConnected"
                @disconnected="onDidDisconnected"
                @guest="continueAsGuest"
            />
        </div>

        <!-- Join Form (shown after DID connected or guest mode) -->
        <div v-else>
            <div class="join-card">
                <form aria-label="Join Session Form" @submit.prevent="joinRoom">
                    <input type="text" id="dc-lroom-handle" name="dc-lroom-handle" placeholder="Shown to everyone in the room"
                        autocomplete="off" data-lpignore="true" data-1p-ignore data-form-type="other" aria-label="How you appear to others" required v-model="name" :disabled="didConnected" />
                    <button type="submit">{{ isJoining ? 'Joining...' : 'Join' }}</button>
                </form>
            </div>
            <p v-if="isLoggedIn" class="did-status">Joining as {{ name }}</p>
            <p v-else-if="didConnected" class="did-status">Joining with verified DecastID</p>
            <p v-else class="did-status guest">Joining as guest · <span class="link" @click="showDidLogin = true">use DID instead</span></p>
        </div>

        <div class="divider">OR</div> <a class="login-link" @click="redirectToLoginPage">Login to Decast</a>
    </main>
</template>

<script>
import BroadcastIcon from '../../icons/BroadcastIcon.vue';
import DidLogin from '@/components/common/DidLogin.vue';
import constants from '@/../constant.js';
import axios from '../../../axios';

export default {
    data() {
        return {
            loggedOut: false,
            name: '',
            roomId: this.$route.params.meeting_id,
            code: '',
            eventName: '',
            creator: '',
            castType: '',
            disabled: false,
            isJoining: false,
            // DID Login state
            didConnected: false,
            connectedDid: '',
            showDidLogin: true,
            // Logged in user state
            isLoggedIn: false,
        };
    },
    components: {
        BroadcastIcon,
        DidLogin,
    },
    mounted() {
        document.getElementById('loading-bg').style.display = 'block';
        this.checkLoginStatus();
        this.getUserDetails();
    },
    computed: {
        activeUserInfo() {
            return this.$store.state.AppActiveUser;
        },
    },
    methods: {
        redirectToHomePage() {
            this.$router.push('/');
        },
        redirectToLoginPage() {
            this.$router.push('/v2Login');
        },
        async saveUser() {
            await fetch(`${this.$globals.transcriptionBaseUrl}/api/audio/save_name/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: this.name,
                    public_meeting_id: this.roomId,
                }),
            });
        },
        joinRoom() {
            if (this.isJoining) {
                return;
            }
            this.isJoining = true;
            if (this.name === '') {
                this.$vs.notify({
                    title: 'Name is required',
                    color: 'danger',
                });
                this.isJoining = false;
                return;
            }
            fetch(`${constants.apiRoomUrl}/api/join/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: this.name,
                    public_meeting_id: this.roomId,
                    password: this.code,
                }),
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    return response.json();
                })
                .then(data => {
                    console.log('Join API response:', data);
                    const roomUrl = data.room_url;
                    this.saveUser()
                    window.location.href = roomUrl;
                    this.isJoining = false;
                    this.$posthog.trackRoomJoined(this.roomId);
                })
                .catch(error => {
                    console.error('Join API error:', error);
                    this.isJoining = false;
                    this.$vs.notify({
                        title: 'OOPS',
                        text: error.message,
                        color: 'danger',
                    });
                });
        },
        async getUserDetails() {
            const config = {
                method: 'get',
                maxBodyLength: Infinity,
                url: `${constants.apiRoomUrl}/api/room/fetch/info/?room_id=${this.roomId}`,
            };

            try {
                const response = await axios(config);
                const responseData = response.data;
                this.eventName = responseData.details.event_name;
                this.creator = responseData.details.creator_name;
                this.castType = responseData.details.cast_type;
                document.getElementById('loading-bg').style.display = 'none';
            } catch (error) {
                console.error(error);
            }
        },
        // DID Login handlers
        onDidConnected(data) {
            this.didConnected = true;
            this.connectedDid = data.did;
            this.name = data.displayName;
            this.showDidLogin = false;
            this.$vs.notify({
                title: 'Connected',
                text: `Logged in as ${data.displayName}`,
                color: 'success',
            });
        },
        onDidDisconnected() {
            this.didConnected = false;
            this.connectedDid = '';
            this.name = '';
            this.showDidLogin = true;
        },
        continueAsGuest() {
            this.showDidLogin = false;
            this.didConnected = false;
        },
        checkLoginStatus() {
            const accessToken = localStorage.getItem('accessToken');
            const userInfo = localStorage.getItem('userInfo');
            if (accessToken && userInfo) {
                try {
                    const user = JSON.parse(userInfo);
                    this.isLoggedIn = true;
                    this.name = user.firstName || user.username || '';
                    this.showDidLogin = false;
                } catch (e) {
                    this.isLoggedIn = false;
                }
            } else {
                this.isLoggedIn = false;
            }
        },
    }
}
</script>

<style scoped>
*:not(i) {
    font-family: 'Rethink Sans' !important;
}

.invite-container {
    text-align: center;
    max-width: 500px;
    width: 100%;
    padding: 20px;
}

h1 {
    font-size: 20px;
    margin-bottom: 0.75rem;
    color: rgba(255, 255, 255, 80%);
    line-height: 1.6;
    text-align: left;
}

h1 .host {
    color: #D3CA57;
    font-weight: 600;
}

h1 .role {
    color: #D3CA57;
    font-weight: 600;
}

.session-card {
    border: 1px solid rgba(255, 255, 255, 10%);
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0;
    display: flex;
    align-items: center;
}

.session-card-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 10px;
}

.session-card-name {
    font-size: 16px;
    color: #FFFFFF;
    font-weight: 600;
}

.session-card-lb {
    font-size: 14px;
    color: rgba(255, 255, 255, 70%);
    font-weight: 400;
}

.status-dot {
    height: 10px;
    width: 10px;
    background-color: #5EC16A;
    border-radius: 50%;
    display: inline-block;
    margin-right: 8px;
}

form {
    width: 100%;
    display: flex;
    margin: 15px 0;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 10%);
    border-radius: 8px;
}

.join-card {
    display: flex;
    width: 100%;
    justify-content: space-between;
}

.did-btn {
    border: 1px solid rgba(255, 255, 255, 50%);
    border-radius: 8px;
    background-color: #15161F;
    margin: 15px 0px 15px 10px;
    padding: 0px;
    height: 40px;
    width: 40px;
    min-width: 40px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
}

.did-btn svg {
    height: 16px;
    width: 16px;
}

input[type="text"] {
    flex: 1;
    padding: 10px;
    border-radius: 8px 0 0 8px;
    border: none;
    outline: none;
    background-color: #15161F;
    color: #FFFFFF;
    font-size: 1rem;
}

button {
    background-color: #D3CA57;
    border: none;
    padding: 10px 20px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 0 8px 8px 0;
    cursor: pointer;
    transition: background 0.2s ease;
}

/* button:hover {
    background-color: #ffbf00;
} */

.divider {
    margin: 20px 0;
    display: flex;
    align-items: center;
    text-align: center;
    color: #999;
    font-size: 0.85rem;
}

.divider::before,
.divider::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #2b2b33;
}

.divider:not(:empty)::before {
    margin-right: 0.75em;
}

.divider:not(:empty)::after {
    margin-left: 0.75em;
}

.login-link {
    color: #FFFFFF;
    border-bottom: 1px solid #FFFFFF;
    text-decoration: none;
    font-weight: 500;
}

.login-link:hover {
    text-decoration: underline;
}

/* DID Login Section */
.did-login-section {
    margin: 20px 0;
}

.did-status {
    color: #5EC16A;
    font-size: 13px;
    margin-top: 10px;
    text-align: left;
}

.did-status.guest {
    color: rgba(255, 255, 255, 50%);
}

.did-status .link {
    color: #D3CA57;
    cursor: pointer;
    text-decoration: underline;
}

.did-status .link:hover {
    color: #ebe369;
}
</style>
